import React, { useCallback, useEffect, useState } from 'react';
import { useBeforeUnload, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface UseUnsavedChangesGuardOptions {
  when: boolean;
  title?: string;
  description?: string;
}

export function useUnsavedChangesGuard({
  when,
  title = 'Leave this page?',
  description = 'You have unsaved changes. If you leave now, your entered data will be lost.',
}: UseUnsavedChangesGuardOptions) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isBypassed, setIsBypassed] = useState(false);
  const isGuardActive = when && !isBypassed;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!isGuardActive) return;
        event.preventDefault();
        event.returnValue = '';
      },
      [isGuardActive]
    )
  );

  useEffect(() => {
    if (!isGuardActive) return;

    const handleDocumentClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.target && anchor.target !== '_self') return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;

      const nextPath = `${url.pathname}${url.search}${url.hash}`;
      const currentPath = `${location.pathname}${location.search}${location.hash}`;
      if (nextPath === currentPath) return;

      event.preventDefault();
      setPendingAction(() => () => navigate(nextPath));
      setDialogOpen(true);
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [isGuardActive, location.hash, location.pathname, location.search, navigate]);

  const confirmNavigation = useCallback(
    (action: () => void) => {
      if (!when) {
        action();
        return;
      }

      setPendingAction(() => action);
      setDialogOpen(true);
    },
    [when]
  );

  const proceedWithoutPrompt = useCallback((action: () => void) => {
    setIsBypassed(true);
    action();
    setTimeout(() => {
      setIsBypassed(false);
    }, 0);
  }, []);

  const handleStay = useCallback(() => {
    setDialogOpen(false);
    setPendingAction(null);
  }, []);

  const handleLeave = useCallback(() => {
    const action = pendingAction;

    setDialogOpen(false);
    setPendingAction(null);

    action?.();
  }, [pendingAction]);

  const confirmationDialog = (
    <AlertDialog open={dialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleStay}>Stay</AlertDialogCancel>
          <AlertDialogAction onClick={handleLeave}>Leave</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirmNavigation, confirmationDialog, proceedWithoutPrompt };
}
